import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';

interface HeroSectionProps {
  onGetStarted: () => void;
  onSeeHow: () => void;
}

export function HeroSection({ onGetStarted, onSeeHow }: HeroSectionProps) {
  const rm = useReducedMotion();

  const fadeUp = (delay = 0) => ({
    initial:    { opacity: 0, y: rm ? 0 : 20 },
    animate:    { opacity: 1, y: 0 },
    transition: { duration: rm ? 0 : 0.86, delay, ease: [0.22, 1, 0.36, 1] as const },
  });

  return (
    <section
      id="product"
      className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-visible"
      style={{
        /* Extra breathing room prevents any clip at varied viewport heights */
        paddingTop:    'clamp(128px, 17vw, 188px)',
        paddingBottom: 'clamp(108px, 13vw, 164px)',
      }}
    >
      {/* ── Center logo glow — pushed deeper into the scene, subordinate ── */}
      <motion.div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 will-change-transform"
        style={{
          /* Lowered so it stays behind the headline, not through it */
          top: '24%',
          width: 'min(58vw, 620px)',
          zIndex: 1,
        }}
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 0.50, scale: 1 }}
        transition={rm ? { duration: 0 } : { duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <img
          src="/assets/tension/hero/hero-center-logo-glow.png"
          alt=""
          aria-hidden
          className="h-auto w-full select-none"
          style={{
            mixBlendMode: 'screen',
            /* Further softened — atmosphere only, no visual weight */
            filter: 'brightness(0.82) saturate(0.88) blur(1px)',
          }}
          draggable={false}
        />
      </motion.div>

      {/* ── App preview — deep in the environment, xl+ only ── */}
      <motion.div
        className="pointer-events-none absolute hidden xl:block"
        style={{
          right: 'max(2%, calc(50% - 620px))',
          top: '27%',
          width: 'min(21vw, 290px)',
          zIndex: 3,
        }}
        initial={{ opacity: 0, x: 22, y: 12 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={rm ? { duration: 0 } : { duration: 1.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Wide ambient haze — bleeds into scene atmosphere */}
        <div
          className="absolute pointer-events-none"
          style={{
            inset: '-40px -50px -60px -50px',
            background: 'radial-gradient(ellipse 70% 65% at 50% 58%, rgba(155,79,232,.16), transparent 72%)',
            filter: 'blur(28px)',
            zIndex: -1,
          }}
        />

        <motion.img
          src="/assets/tension/hero/homepage-hero-app-preview.png"
          alt="TENSION app preview"
          className="relative h-auto w-full select-none"
          style={{
            borderRadius: 26,
            /* Softer, more environmental shadow */
            boxShadow: [
              '0 32px 80px rgba(0,0,0,.48)',
              '0 0 36px rgba(155,79,232,.09)',
              '0 0 0 1px rgba(255,255,255,.055)',
            ].join(', '),
            /* Fade edges into atmosphere — no hard border */
            maskImage: [
              'radial-gradient(ellipse 86% 92% at 50% 46%, black 52%, transparent 100%)',
            ].join(', '),
            WebkitMaskImage: [
              'radial-gradient(ellipse 86% 92% at 50% 46%, black 52%, transparent 100%)',
            ].join(', '),
            opacity: 0.88,
          }}
          animate={rm ? undefined : {
            y: [0, -7, 0],
            rotate: [-0.8, -0.2, -0.8],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          draggable={false}
        />
      </motion.div>

      {/* ── Primary copy — centred, protected from clipping ── */}
      <div
        className="relative z-10 mx-auto flex flex-col items-center px-5 text-center"
        style={{ maxWidth: 'min(820px, 90vw)' }}
      >
        <motion.p className="t-eyebrow mb-7" {...fadeUp(0.10)}>
          Relationship Intelligence
        </motion.p>

        <motion.h1
          className="t-display mb-8"
          style={{ letterSpacing: '-0.038em' }}
          {...fadeUp(0.20)}
        >
          <span className="block" style={{ color: 'rgba(244,246,252,.96)' }}>
            For the people who
          </span>
          <span className="t-gradient-text block">matter most.</span>
        </motion.h1>

        <motion.p
          className="t-body-lg mx-auto mb-11"
          style={{ maxWidth: 560 }}
          {...fadeUp(0.30)}
        >
          Understand communication, trust, emotional rhythm, and connection
          across the relationships that shape your life.
        </motion.p>

        {/* CTA row */}
        <motion.div
          className="flex flex-col items-center gap-3 sm:flex-row"
          {...fadeUp(0.40)}
        >
          <motion.button
            type="button"
            onClick={onGetStarted}
            className="t-btn-primary group inline-flex items-center gap-2.5"
            style={{ paddingInline: 38, paddingBlock: 16, borderRadius: 18, fontSize: 15 }}
            whileHover={rm ? undefined : { y: -2, scale: 1.03 }}
            whileTap={rm ? undefined : { scale: 0.975 }}
            transition={{ type: 'spring', stiffness: 380, damping: 26 }}
          >
            Start Your Journey
            <ArrowRight className="h-4 w-4 stroke-[2.5] transition-transform duration-200 group-hover:translate-x-0.5" />
          </motion.button>

          <motion.button
            type="button"
            onClick={onSeeHow}
            className="t-btn-secondary inline-flex items-center gap-2.5"
            style={{ paddingInline: 34, paddingBlock: 15, borderRadius: 18, fontSize: 15 }}
            whileHover={rm ? undefined : { y: -1, scale: 1.015 }}
            whileTap={rm ? undefined : { scale: 0.985 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          >
            <Play className="h-3.5 w-3.5 fill-current stroke-none" style={{ color: '#9B4FE8', opacity: 0.88 }} />
            See How It Works
          </motion.button>
        </motion.div>

        <motion.p
          className="mt-6 text-[12px] font-medium tracking-[0.12em]"
          style={{ color: 'rgba(244,246,252,.28)' }}
          {...fadeUp(0.50)}
        >
          Private beta · No credit card · Early access worldwide
        </motion.p>
      </div>

      {/* ── Intelligence preview dock ── */}
      <motion.div
        className="relative z-10 mx-auto mt-20 w-full px-5"
        style={{ maxWidth: 'min(680px, 90vw)' }}
        {...fadeUp(0.56)}
      >
        <div
          className="rounded-[22px]"
          style={{
            padding: 'clamp(22px, 3.2vw, 32px) clamp(24px, 4.2vw, 40px)',
            background: 'rgba(4, 8, 20, 0.82)',
            border: '1px solid rgba(255,255,255,.072)',
            backdropFilter: 'blur(36px) saturate(1.5)',
            WebkitBackdropFilter: 'blur(36px) saturate(1.5)',
            boxShadow: [
              '0 1px 0 rgba(255,255,255,.11) inset',
              '0 -1px 0 rgba(155,79,232,.06) inset',
              '0 4px 16px rgba(0,0,0,.44)',
              '0 28px 72px rgba(0,0,0,.62)',
              '0 0 0 1px rgba(255,255,255,.038)',
              '0 0 56px rgba(155,79,232,.07)',
            ].join(', '),
          }}
        >
          <div className="mb-5 flex items-center gap-2.5">
            <span
              className="inline-block h-[7px] w-[7px] animate-pulse rounded-full"
              style={{ backgroundColor: '#3DD68C', boxShadow: '0 0 6px rgba(61,214,140,.55)' }}
            />
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em]"
               style={{ color: 'rgba(244,246,252,.36)' }}>
              Live Intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {[
              { label: 'Connection Score',  value: '84',            sub: '↑ Rising'           },
              { label: 'Pattern Signal',     value: '3-day pattern', sub: 'Detected this week' },
              { label: 'Relationship Pulse', value: 'Stable',        sub: 'Sync improving'     },
            ].map((m) => (
              <div key={m.label} className="text-center sm:text-left">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em]"
                   style={{ color: 'rgba(244,246,252,.38)' }}>
                  {m.label}
                </p>
                <p className="mt-2 text-[20px] font-bold" style={{ color: '#F4F6FC', letterSpacing: '-0.01em' }}>
                  {m.value}
                </p>
                <p
                  className="mt-0.5 text-[11px] font-semibold"
                  style={{
                    background: 'linear-gradient(90deg, #E8179B, #9B4FE8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {m.sub}
                </p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── Scroll indicator ── */}
      <motion.div
        className="absolute bottom-10 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2.5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1.2 }}
        aria-hidden
      >
        <motion.div
          className="h-10 w-px"
          style={{ background: 'linear-gradient(180deg, transparent, rgba(155,79,232,.48), transparent)' }}
          animate={rm ? undefined : { scaleY: [1, 1.4, 1], opacity: [0.35, 0.80, 0.35] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <p className="text-[10px] font-semibold tracking-[0.26em]"
           style={{ color: 'rgba(244,246,252,.24)' }}>
          SCROLL
        </p>
      </motion.div>
    </section>
  );
}
